/**
 * ChapterSelectionModal - DRM-aware chapter selection for offline downloads
 *
 * Features:
 * - Shows chapter list with checkboxes
 * - Displays DRM quota status
 * - Disables chapters that would exceed quota
 * - Shows already downloaded chapters (checked, disabled)
 * - Shows quota reset information
 * - Estimates download size
 *
 * Mobile-first design with bottom sheet on mobile, modal on desktop
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Info, CheckCircle, Lock, AlertCircle } from 'lucide-react';
import type { ChapterSelectionInfo } from '@/types/offline';
import type { BookMetadataDto } from '@/types/book';

export interface ChapterSelectionModalProps {
  bookMetadata: BookMetadataDto;
  chapterSelectionInfo: ChapterSelectionInfo[];
  availableQuota: number;
  quotaExceeded: boolean;
  isDrmEnabled: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedChapterIndices: number[]) => void;
}

export function ChapterSelectionModal({
  bookMetadata,
  chapterSelectionInfo,
  availableQuota,
  quotaExceeded,
  isDrmEnabled,
  isOpen,
  onClose,
  onConfirm,
}: ChapterSelectionModalProps) {
  const { t } = useTranslation();
  const [selectedChapters, setSelectedChapters] = useState<Set<number>>(new Set());
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      dialogRef.current?.focus();
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus?.();
    }
  }, [isOpen]);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedChapters(new Set());
    }
  }, [isOpen]);

  // Calculate downloadable chapters
  const downloadableChapters = useMemo(() => {
    return chapterSelectionInfo.filter(chapter => !chapter.isDownloaded);
  }, [chapterSelectionInfo]);

  // Calculate how many can be selected
  const maxSelectableCount = isDrmEnabled ? availableQuota : downloadableChapters.length;

  // Estimate download size (rough estimate: 500KB per chapter)
  const estimatedSizeBytes = selectedChapters.size * 500 * 1024;
  const estimatedSizeMB = (estimatedSizeBytes / (1024 * 1024)).toFixed(2);

  const handleToggleChapter = (chapterIndex: number) => {
    const chapter = chapterSelectionInfo[chapterIndex];

    // Can't toggle if already downloaded
    if (chapter.isDownloaded) {
      return;
    }

    const newSelection = new Set(selectedChapters);

    if (newSelection.has(chapterIndex)) {
      // Deselect
      newSelection.delete(chapterIndex);
    } else {
      // Select (if quota allows)
      if (newSelection.size < maxSelectableCount) {
        newSelection.add(chapterIndex);
      }
    }

    setSelectedChapters(newSelection);
  };

  const handleSelectAll = () => {
    const newSelection = new Set<number>();
    let count = 0;

    for (const chapter of downloadableChapters) {
      if (count < maxSelectableCount) {
        newSelection.add(chapter.chapterIndex);
        count++;
      }
    }

    setSelectedChapters(newSelection);
  };

  const handleDeselectAll = () => {
    setSelectedChapters(new Set());
  };

  const handleConfirm = () => {
    const selectedIndices = Array.from(selectedChapters);
    onConfirm(selectedIndices);
  };

  const canConfirm = selectedChapters.size > 0;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            role="presentation"
            onKeyDown={(e) => e.key === 'Escape' && onClose()}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            {/* Modal Content - Centered modal (matching SettingsPanel) */}
            <motion.div
              ref={dialogRef}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="chapter-selection-title"
              tabIndex={-1}
              className="bg-white dark:bg-neutral-900 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
            >
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  <h2 id="chapter-selection-title" className="text-xl font-semibold text-neutral-800 dark:text-neutral-100">
                    {t('App.Offline.ChapterSelectionModal.Title')}
                  </h2>
                </div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 truncate">
                  {bookMetadata.title}
                </p>
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-colors ml-2"
              >
                <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
              </button>
            </div>

            {/* DRM Quota Status */}
            {isDrmEnabled && (
              <div className="px-6 py-4 bg-primary-50 dark:bg-primary-900/20 border-b border-primary-100 dark:border-primary-800">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary-900 dark:text-primary-100">
                      {t('App.Offline.ChapterSelectionModal.DrmQuotaTitle')}
                    </p>
                    {quotaExceeded ? (
                      <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                        {t('App.Offline.ChapterSelectionModal.QuotaExhausted')}
                      </p>
                    ) : (
                      <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                        {t('App.Offline.ChapterSelectionModal.QuotaAvailable', { count: availableQuota })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Selection Controls */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
              <div className="text-sm text-neutral-600 dark:text-neutral-400">
                {t('App.Offline.ChapterSelectionModal.SelectionCount', { selected: selectedChapters.size, max: maxSelectableCount })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAll}
                  disabled={quotaExceeded || maxSelectableCount === 0}
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('App.Offline.ChapterSelectionModal.SelectAll')}
                </button>
                <span className="text-neutral-300 dark:text-neutral-600">|</span>
                <button
                  onClick={handleDeselectAll}
                  disabled={selectedChapters.size === 0}
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('App.Offline.ChapterSelectionModal.DeselectAll')}
                </button>
              </div>
            </div>

            {/* Chapter List - Scrollable content area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              {chapterSelectionInfo.map((chapter, index) => {
                const isSelected = selectedChapters.has(chapter.chapterIndex);
                const isDownloaded = chapter.isDownloaded;
                const canDownload = chapter.canDownload && !quotaExceeded;
                const isDisabled = isDownloaded || !canDownload || (!isSelected && selectedChapters.size >= maxSelectableCount);

                return (
                  <button
                    key={chapter.chapterIndex}
                    onClick={() => handleToggleChapter(chapter.chapterIndex)}
                    disabled={isDisabled && !isSelected}
                    className={`
                      w-full flex items-start gap-3 p-4 border-b border-neutral-200 dark:border-neutral-800
                      transition-colors
                      ${isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-neutral-50 dark:hover:bg-neutral-800'}
                      ${(isDisabled && !isSelected) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    {/* Checkbox / Status Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {isDownloaded ? (
                        <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
                      ) : isSelected ? (
                        <div className="w-5 h-5 rounded border-2 border-primary-600 dark:border-primary-400 bg-primary-600 dark:bg-primary-400 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : canDownload ? (
                        <div className="w-5 h-5 rounded border-2 border-neutral-300 dark:border-neutral-600" />
                      ) : (
                        <Lock className="w-5 h-5 text-neutral-400 dark:text-neutral-600" />
                      )}
                    </div>

                    {/* Chapter Info */}
                    <div className="flex-1 min-w-0 text-left">
                      <p className={`text-sm font-medium ${isDownloaded ? 'text-success-700 dark:text-success-300' : 'text-neutral-900 dark:text-white'}`}>
                        {t('App.Offline.ChapterSelectionModal.ChapterLabel', { number: index + 1, title: chapter.title })}
                      </p>
                      {isDownloaded && chapter.downloadedAt && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                          {t('App.Offline.ChapterSelectionModal.DownloadedAt', { date: new Date(chapter.downloadedAt).toLocaleDateString() })}
                        </p>
                      )}
                      {!isDownloaded && !canDownload && (
                        <p className="text-xs text-error-600 dark:text-error-400 mt-1">
                          {t('App.Offline.ChapterSelectionModal.QuotaExceeded')}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-neutral-50 dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700 px-6 py-4">
              {/* Download Size Estimate */}
              {selectedChapters.size > 0 && (
                <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{t('App.Offline.ChapterSelectionModal.EstimatedSize', { size: estimatedSizeMB })}</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                >
                  {t('App.Offline.ChapterSelectionModal.Cancel')}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {t('App.Offline.ChapterSelectionModal.Download', { count: selectedChapters.size })}
                </button>
              </div>
            </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
